// src/routes/routes.js
// -----------------------------------------------------------------------------
// Configuration centralisée des routes + métadonnées d'accès par rôle.
// (imports corrigés pour TeamsList / TeamDetail + pages légales/support sous layouts/)
// -----------------------------------------------------------------------------

import Dashboard from "layouts/dashboard";
import Clients from "layouts/clients"; // (import existant, pas utilisé ci-dessous)
import AddUserForm from "layouts/add-user-form";
import UserTable from "layouts/user-table";
import RTL from "layouts/rtl"; // (import existant, pas utilisé ci-dessous)
import Profile from "layouts/profile";
import SignIn from "layouts/authentication/sign-in";
import SignUp from "layouts/authentication/sign-up";
import Overview from "layouts/overview"; // (import existant, pas utilisé ci-dessous)
import AllSales from "layouts/all-sales";
import Pledges from "layouts/pledges";
import Estimates from "layouts/estimates";
import SalesOrders from "layouts/sales-orders";
import ProductsAndServices from "layouts/products-and-services";
import Expenditures from "layouts/expenditures";
import Bills from "layouts/bills";
import Vendors from "layouts/vendors";
import Reports from "layouts/reports";
import ReportViewer from "layouts/reports/ReportViewer";
import ReportEditor from "layouts/reports/ReportEditor";
import AssignReport from "layouts/reports/AssignReport";
import BudgetVsActuals from "layouts/reports/BudgetVsActuals";
import Forecasts from "layouts/forecasts";
import Budgets from "layouts/budgets";
import BudgetEditor from "layouts/budgets/BudgetEditor";

// ✅ Ajout des imports manquants (corrige les erreurs eslint react/jsx-no-undef)
import TeamsList from "layouts/teams/TeamsList";
import TeamDetail from "layouts/teams/TeamDetail";

// ✅ Les pages légales & support sont sous layouts/
import Terms from "layouts/legal/Terms";
import Privacy from "layouts/legal/Privacy";
import Eula from "layouts/legal/Eula";
import Support from "layouts/support/Support";

import { ROLE } from "context/roles";
import Forbidden403 from "layouts/error/Forbidden403";
import Icon from "@mui/material/Icon";

const routes = [
  // ───────────────── Forbidden403 ─────────────────
  {
    type: "route",
    name: "Forbidden",
    key: "forbidden",
    route: "/403",
    component: <Forbidden403 />,
  },

  // ───────────────── Dashboard ─────────────────
  {
    type: "collapse",
    name: "Dashboard",
    key: "dashboard",
    icon: <Icon fontSize="small">dashboard</Icon>,
    route: "/dashboard",
    component: <Dashboard />,
    allowedRoles: [ROLE.ED, ROLE.GA, ROLE.SUP, ROLE.PD],
  },

  // ───────────────── Sales ─────────────────
  {
    type: "collapse",
    name: "Sales",
    key: "sales",
    icon: <Icon fontSize="small">shopping_cart</Icon>,
    allowedRoles: [ROLE.ED, ROLE.GA],
    collapse: [
      { name: "All Sales", key: "all-sales", route: "/all-sales", component: <AllSales />, allowedRoles: [ROLE.ED, ROLE.GA] },
      { name: "Pledges", key: "pledges", route: "/pledges", component: <Pledges />, allowedRoles: [ROLE.ED, ROLE.GA] },
      { name: "Estimates", key: "estimates", route: "/estimates", component: <Estimates />, allowedRoles: [ROLE.ED, ROLE.GA] },
      { name: "Sales Orders", key: "sales-orders", route: "/sales-orders", component: <SalesOrders />, allowedRoles: [ROLE.ED, ROLE.GA] },
      { name: "Products & Services", key: "products-services", route: "/products-services", component: <ProductsAndServices />, allowedRoles: [ROLE.ED, ROLE.GA] },
    ],
  },

  // ───────────────── Expenditure ─────────────────
  {
    type: "collapse",
    name: "Expenditure",
    key: "expenditure-menu",
    icon: <Icon fontSize="small">account_balance_wallet</Icon>,
    allowedRoles: [ROLE.ED, ROLE.GA],
    collapse: [
      { name: "Expenditures", key: "expenditures", route: "/expenditures", component: <Expenditures />, allowedRoles: [ROLE.ED, ROLE.GA] },
      { name: "Bills", key: "bills", route: "/bills", component: <Bills />, allowedRoles: [ROLE.ED, ROLE.GA] },
      { name: "Vendors", key: "vendors", route: "/vendors", component: <Vendors />, allowedRoles: [ROLE.ED, ROLE.GA] },
    ],
  },

  // ───────────────── Financial Planning ─────────────────
  {
    type: "collapse",
    name: "Financial Planning",
    key: "financial-planning",
    icon: <Icon fontSize="small">insights</Icon>,
    allowedRoles: [ROLE.ED, ROLE.GA, ROLE.SUP],
    collapse: [
      { name: "Forecasts", key: "forecasts", route: "/forecasts", component: <Forecasts />, allowedRoles: [ROLE.ED, ROLE.GA, ROLE.SUP] },
      { name: "Budgets", key: "budgets", route: "/budgets", component: <Budgets />, allowedRoles: [ROLE.ED, ROLE.GA, ROLE.SUP] },
    ],
  },

  // Éditeur budget
  { type: "route", name: "Budget Editor", key: "budget-editor", route: "/budgets/edit", component: <BudgetEditor />, allowedRoles: [ROLE.ED, ROLE.GA] },

  // ───────────────── Reports ─────────────────
  {
    type: "collapse",
    name: "Reports",
    key: "reports",
    icon: <Icon fontSize="small">bar_chart</Icon>,
    route: "/reports",
    component: <Reports />,
    allowedRoles: [ROLE.ED, ROLE.GA, ROLE.SUP, ROLE.PD],
  },
  { type: "route", name: "Budget vs Actuals", key: "budget-vs-actuals", route: "/reports/budget-vs-actuals", component: <BudgetVsActuals />, icon: <Icon fontSize="small">assessment</Icon>, allowedRoles: [ROLE.ED, ROLE.GA, ROLE.SUP, ROLE.PD] },
  { type: "route", name: "Report Viewer", key: "report-viewer", route: "/reports/view/:reportId", component: <ReportViewer />, allowedRoles: [ROLE.ED, ROLE.GA, ROLE.SUP, ROLE.PD] },
  { type: "route", name: "Report Editor", key: "report-editor", route: "/reports/edit/:reportId", component: <ReportEditor />, allowedRoles: [ROLE.ED, ROLE.GA] },

  // Assignation
  { type: "route", name: "Assign Report (global)", key: "assign-report-global", route: "/reports/assign", component: <AssignReport />, allowedRoles: [ROLE.ED, ROLE.GA, ROLE.SUP] },
  { type: "route", name: "Assign Report", key: "assign-report", route: "/reports/assign/:reportId", component: <AssignReport />, allowedRoles: [ROLE.ED, ROLE.GA, ROLE.SUP] },

  // ───────────────── Teams ─────────────────
  { type: "collapse", name: "Teams", key: "teams", icon: <Icon fontSize="small">groups</Icon>, route: "/teams", component: <TeamsList />, allowedRoles: [] },
  { type: "route", name: "Team Detail", key: "team-detail", route: "/teams/:teamId", component: <TeamDetail />, allowedRoles: [] },

  // ───────────────── Users ─────────────────
  {
    type: "collapse",
    name: "Users",
    key: "users-dropdown",
    icon: <Icon fontSize="small">people</Icon>,
    allowedRoles: [ROLE.ED, ROLE.GA],
    collapse: [
      { name: "Add", key: "users-add", route: "/add-user", icon: <Icon fontSize="small">person_add</Icon>, component: <AddUserForm />, allowedRoles: [ROLE.ED, ROLE.GA] },
      { name: "List of users", key: "users-list", route: "/users", icon: <Icon fontSize="small">list</Icon>, component: <UserTable />, allowedRoles: [ROLE.ED, ROLE.GA] },
    ],
  },

  // ───────────────── Profile ─────────────────
  { type: "collapse", name: "Profile", key: "profile", icon: <Icon fontSize="small">person</Icon>, route: "/profile", component: <Profile />, allowedRoles: [ROLE.ED, ROLE.GA, ROLE.SUP, ROLE.PD] },

  // ───────────────── Public (non menu) ─────────────────
  { type: "route", name: "Sign In", key: "sign-in", route: "/authentication/sign-in", component: <SignIn /> },
  { type: "route", name: "Sign Up", key: "sign-up", route: "/authentication/sign-up", component: <SignUp /> },

  // ───────────────── Légales & Support (publiques) ─────────────────
  { type: "route", name: "Terms", key: "terms", route: "/legal/terms", component: <Terms /> },
  { type: "route", name: "Privacy", key: "privacy", route: "/legal/privacy", component: <Privacy /> },
  { type: "route", name: "Eula", key: "eula", route: "/legal/eula", component: <Eula /> },
  { type: "route", name: "Support", key: "support", route: "/support", component: <Support /> },
];

export default routes;
